import { useEffect, useState } from 'react';
import { logger } from '@/core/logging';
import { getOrganizationTree } from '../api/organizationHierarchy.service';
import type { HierarchyCounts, OrganizationRecord, OrganizationTreeRecord } from '../models';

/** Counts every level of the tree in one walk -- the same numbers the platform's `counts` carries. */
export function countHierarchy(tree: OrganizationTreeRecord): HierarchyCounts {
  const counts: HierarchyCounts = { entities: 0, businessUnits: 0, divisions: 0, departments: 0 };
  for (const entity of tree.legalEntities) {
    counts.entities += 1;
    for (const businessUnit of entity.businessUnits) {
      counts.businessUnits += 1;
      for (const division of businessUnit.divisions) {
        counts.divisions += 1;
        counts.departments += division.departments.length;
      }
    }
  }
  return counts;
}

/**
 * The organization's entity / business unit / division / department counts.
 *
 * `GET /organizations/{code}` carries them in `counts`, and when it does nothing is fetched. A platform that
 * predates that field -- or one still running a build from before it -- answers without it, and then they are
 * counted from `GET /organizations/{code}/tree`, the read the setup's Entity Setup step draws, so the card
 * shows real numbers either way.
 *
 * `null` while counting, or when neither source answers; the card shows a dash rather than a guessed zero.
 */
export function useOrganizationCounts(organization: OrganizationRecord | null): HierarchyCounts | null {
  const carried = organization?.counts ?? null;
  const code = organization?.organizationCode ?? null;
  const [counted, setCounted] = useState<HierarchyCounts | null>(null);

  useEffect(() => {
    setCounted(null);
    if (carried || !code) return undefined;

    let cancelled = false;
    void (async () => {
      try {
        const tree = await getOrganizationTree(code);
        if (!cancelled) setCounted(countHierarchy(tree));
      } catch (err: unknown) {
        logger.warn('Could not count the organization from its tree', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [carried, code]);

  return carried ?? counted;
}
